import * as assetGroupsApi from '@/api/storageForecasting/AssetGroupsApi.ts';
import { inputApi } from '@/api/storageForecasting/InputApi.ts';
import {
    DEFAULT_STORECAST_SESSION_CHART_OVERRIDES,
} from '@/constants/forecasting/assetConstants.ts';
import { isLegacyAssetGroup } from '@/utils/storageForecasting/assetUtils.ts';
import { tryCatch } from '@/helpers/tryCatch.ts';
import { IAssumptionDetails } from '@/interfaces/storageForecasting/IAssumptionDetails.ts';
import { createAssetGroupFromPreset, IAssetGroup } from '@/interfaces/storageForecasting/IAssetGroup.ts';
import type { IAssetGroupCreateRequest } from '@/interfaces/storageForecasting/IAssetGroupCreateRequest.ts';
import { IAssetGroupsResponse } from '@/interfaces/storageForecasting/IAssetGroupsResponse.ts';
import type { IAssetPreset } from '@/interfaces/storageForecasting/IAssetPreset';
import type { IAssetComparisonSlot } from '@/interfaces/storageForecasting/IAssetComparisonSlot';
import type { IDashboardCommonSettings } from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';
import {
    computed, ref,
} from 'vue';
import type { IRunOutputDto } from '@/interfaces/storageForecasting/IRunOutput.ts';
import { IAssetInputDto } from '@/interfaces/storageForecasting/IAsset.ts';
import { adminApi } from '@/api/admin/adminApi.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';

export const useAssetStore = defineStore('assetStore', () => {
    const toast = useToast();

    const comparisonSlotsSessionByGroupId = ref<Record<string, Array<IAssetComparisonSlot>>>({});
    const runDataByRunIdCache = ref<Record<string, IRunOutputDto>>({});
    const chartOverridesSessionByGroupId = ref<Record<string, IDashboardCommonSettings>>({});
    const allPresetAssets = ref<Array<IAssetPreset>>([]);
    const defaultAssetGroups = computed<Array<IAssetGroup>>(() => allPresetAssets.value.map(createAssetGroupFromPreset));
    const visiblePresetAssets = computed<Array<IAssetPreset>>(() => allPresetAssets.value.filter(asset => asset.showInDefaultsList !== false));
    const visibleDefaultAssetGroups = computed<Array<IAssetGroup>>(() => visiblePresetAssets.value.map(createAssetGroupFromPreset));
    const allAvailableInputs = ref<Array<IAssetInputDto>>([]);
    const visibleAvailableInputs = computed<Array<IAssetInputDto>>(() => allAvailableInputs.value.filter(input => input.showInDefaultsList !== false));
    let availableInputsRequest: Promise<void> | null = null;
    let availableInputsRequestId = 0;
    let hasLoadedAvailableInputs = false;
    const assumptionsList = ref<Array<IAssumptionDetails>>([]);
    const powerCurvesAssumptionsList = ref<Array<IAssumptionDetails>>([]);
    let powerCurvesAssumptionsRequest: Promise<void> | null = null;
    let powerCurvesAssumptionsRequestId = 0;
    let hasLoadedPowerCurvesAssumptions = false;
    const applicationVersionsList = ref<Array<string>>([]);
    const availableLongTermPowerCountries = ref<Array<IIdName>>([]);
    const hasLoadedAvailableLongTermPowerCountries = ref<boolean>(false);

    const assetGroupsById = ref<Record<string, IAssetGroup>>({});
    const assetGroupOrder = ref<Array<string>>([]);
    const activeAssetGroupTabId = ref<string | null>(null);
    const hasLoadedAssetGroups = ref<boolean>(false);

    async function getAvailableLongTermPowerCountries (): Promise<void> {
        if (hasLoadedAvailableLongTermPowerCountries.value) return;

        availableLongTermPowerCountries.value = await adminApi.getAvailableLongTermPowerCountries();
        hasLoadedAvailableLongTermPowerCountries.value = true;
    }

    const sortedAssetGroups = computed<Array<IAssetGroup>>(() => assetGroupOrder.value
        .map(id => assetGroupsById.value[id])
        .filter((group): group is IAssetGroup => group !== undefined));

    function applyAssetGroupsResponse (response: IAssetGroupsResponse): void {
        assetGroupsById.value = { ...response.itemsWithOrder.objectsToStore };
        assetGroupOrder.value = [...response.itemsWithOrder.order];
        activeAssetGroupTabId.value = response.activeTab;
        hasLoadedAssetGroups.value = true;
    }

    async function fetchAssetGroups (): Promise<void> {
        if (hasLoadedAssetGroups.value) return;
        const { data, error } = await tryCatch(assetGroupsApi.getAllAssetGroups());
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not load asset groups.', life: 5000,
            });
            return;
        }
        if (data) {
            applyAssetGroupsResponse(data);
        }
    }

    async function createAssetGroup (group: IAssetGroup): Promise<IAssetGroup | null> {
        const request: IAssetGroupCreateRequest = {
            name: group.name,
            description: group.description,
            icon: group.icon,
            iconColour: group.iconColour,
            assetIds: group.assetIds,
            options: group.options,
        };
        const { data: createdGroup, error } = await tryCatch(assetGroupsApi.createAssetGroup(request));
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not create asset group.', life: 5000,
            });
            return null;
        }
        assetGroupsById.value = { ...assetGroupsById.value, [createdGroup.id]: createdGroup };
        assetGroupOrder.value = [createdGroup.id, ...assetGroupOrder.value];
        return createdGroup;
    }

    async function updateAssetGroup (group: IAssetGroup): Promise<boolean> {
        if (defaultAssetGroups.value.some(defaultGroup => defaultGroup.id === group.id)) {
            return false;
        }
        const { error } = await tryCatch(assetGroupsApi.updateAssetGroup(group));
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not save asset group.', life: 5000,
            });
            return false;
        }
        await fetchAssetGroups();
        return true;
    }

    async function deleteAssetGroup (id: string): Promise<void> {
        const group = assetGroupsById.value[id];

        const deletableAssetIds = group?.assetIds.filter(assetId => {
            const input = allAvailableInputs.value.find(i => i.id === assetId);
            if (input) return !input.isReadOnly;
            return !allPresetAssets.value.some(preset => preset.inputIds.includes(assetId));
        }) ?? [];

        if (deletableAssetIds.length && !isLegacyAssetGroup(group)) {
            const deleteResults = await Promise.all(
                deletableAssetIds.map(assetId => tryCatch(inputApi.deleteInput(assetId))),
            );
            if (deleteResults.some(result => result.error)) {
                toast.add({
                    severity: 'error', summary: 'Error', detail: 'Could not delete all assets in this group.', life: 5000,
                });
                return;
            }
        }

        const { error } = await tryCatch(assetGroupsApi.deleteAssetGroup(id));
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not delete asset group.', life: 5000,
            });
            return;
        }
        const nextById = { ...assetGroupsById.value };
        delete nextById[id];
        assetGroupsById.value = nextById;
        assetGroupOrder.value = assetGroupOrder.value.filter(groupId => groupId !== id);
        if (activeAssetGroupTabId.value === id) {
            activeAssetGroupTabId.value = null;
        }

        if (deletableAssetIds.length) {
            await refreshAllAvailableInputs();
        }
    }

    async function reorderAssetGroups (ordering: Array<string>): Promise<void> {
        const { error } = await tryCatch(assetGroupsApi.updateAssetGroupsOrdering({ ordering }));
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not reorder asset groups.', life: 5000,
            });
            return;
        }
        assetGroupOrder.value = [...ordering];
    }

    async function updateActiveAssetGroupTab (id: string): Promise<void> {
        const { error } = await tryCatch(assetGroupsApi.updateActiveAssetGroupTab({ activeTabId: id }));
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not update the active asset group.', life: 5000,
            });
            return;
        }
        activeAssetGroupTabId.value = id;
    }

    function updateComparisonSlotsSessionForGroup (groupId: string, slots: Array<IAssetComparisonSlot>): void {
        comparisonSlotsSessionByGroupId.value[groupId] = slots;
    }

    function updateRunDataByRunIdCache (runId: string, data: IRunOutputDto): void {
        runDataByRunIdCache.value[runId] = data;
    }

    function updateChartOverridesSessionForGroup (
        groupId: string,
        settings: IDashboardCommonSettings,
    ): void {
        chartOverridesSessionByGroupId.value = {
            ...chartOverridesSessionByGroupId.value,
            [groupId]: { ...DEFAULT_STORECAST_SESSION_CHART_OVERRIDES, ...settings },
        };
    }

    async function fetchAssetPresets (): Promise<void> {
        const { data, error } = await tryCatch(inputApi.getAssetPresets());
        if (error) {
            toast.add({
                severity: 'error', summary: 'Error', detail: 'Could not load asset presets.', life: 5000,
            });
            return;
        }
        if (!data) return;

        allPresetAssets.value = data.defaultGroups;
    }

    function requestAvailableInputs (): Promise<void> {
        const requestId = availableInputsRequestId + 1;
        availableInputsRequestId = requestId;
        const request = inputApi.getInputListNew().then((inputs) => {
            if (requestId !== availableInputsRequestId) return;
            allAvailableInputs.value = inputs;
            hasLoadedAvailableInputs = true;
        }).finally(() => {
            if (requestId === availableInputsRequestId) {
                availableInputsRequest = null;
            }
        });
        availableInputsRequest = request;
        return request;
    }

    async function ensureAllAvailableInputs (): Promise<void> {
        if (hasLoadedAvailableInputs) return;
        const request = availableInputsRequest ?? requestAvailableInputs();
        await request;
    }

    async function refreshAllAvailableInputs (): Promise<void> {
        hasLoadedAvailableInputs = false;
        await requestAvailableInputs();
    }

    function requestPowerCurvesAssumptions (): Promise<void> {
        const requestId = powerCurvesAssumptionsRequestId + 1;
        powerCurvesAssumptionsRequestId = requestId;
        const request = inputApi.getPowerCurvesAssumptionList().then((assumptions) => {
            if (requestId !== powerCurvesAssumptionsRequestId) return;
            powerCurvesAssumptionsList.value = assumptions;
            hasLoadedPowerCurvesAssumptions = true;
        }).finally(() => {
            if (requestId === powerCurvesAssumptionsRequestId) {
                powerCurvesAssumptionsRequest = null;
            }
        });
        powerCurvesAssumptionsRequest = request;
        return request;
    }

    async function ensurePowerCurvesAssumptionsList (): Promise<void> {
        if (hasLoadedPowerCurvesAssumptions) return;
        const request = powerCurvesAssumptionsRequest ?? requestPowerCurvesAssumptions();
        await request;
    }

    async function refreshPowerCurvesAssumptionsList (): Promise<void> {
        hasLoadedPowerCurvesAssumptions = false;
        await requestPowerCurvesAssumptions();
    }

    async function fetchAssumptionsList (): Promise<void> {
        assumptionsList.value = await inputApi.getAssumptionsList();
    }

    function getApplicationVersions (): Promise<void> {
        return inputApi.getApplicationVersions().then((applicationVersions) => {
            applicationVersionsList.value = applicationVersions;
        });
    }

    return {
        comparisonSlotsSessionByGroupId,
        runDataByRunIdCache,
        chartOverridesSessionByGroupId,
        allPresetAssets,
        visiblePresetAssets,
        defaultAssetGroups,
        visibleDefaultAssetGroups,
        allAvailableInputs,
        visibleAvailableInputs,
        assumptionsList,
        powerCurvesAssumptionsList,
        applicationVersionsList,
        availableLongTermPowerCountries,
        activeAssetGroupTabId,
        hasLoadedAssetGroups,
        sortedAssetGroups,
        updateComparisonSlotsSessionForGroup,
        updateRunDataByRunIdCache,
        updateChartOverridesSessionForGroup,
        ensureAllAvailableInputs,
        refreshAllAvailableInputs,
        ensurePowerCurvesAssumptionsList,
        refreshPowerCurvesAssumptionsList,
        fetchAssetPresets,
        fetchAssumptionsList,
        getApplicationVersions,
        getAvailableLongTermPowerCountries,
        fetchAssetGroups,
        createAssetGroup,
        updateAssetGroup,
        deleteAssetGroup,
        reorderAssetGroups,
        updateActiveAssetGroupTab,
    };
});
